import React, {
  useCallback, useContext, useEffect, useMemo, useRef,
} from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  Box, Image, Text, Link, ListItem, Tag,
} from '@campaignhub/suit-theme'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { useThunkDispatch } from '@campaignhub/react-hooks'

import Icons from '@components/Icons'
import PageContext from '@contexts/pageContext'
import { getEntityByName } from '@functions/getEntityByName'
import { isFeatureEnabled } from '@functions/features'
import { DELIVERED_STATUS_ID } from '@functions/isCurrentVersionDelivered'
import useBlobMetadata, { AI_GENERATED_META_KEY } from '@hooks/useBlobMetadata'
import useAssetVersionDeletion from '@hooks/useAssetVersionDeletion'
import { loadAiChildren, loadAiLineage } from '@redux/modules/aiLineage'

// CH-4304: child links under the base's version — every AI asset derived from that version,
// clickable like the Source link (selects the child in place).
const ChildrenLinks = (props) => {
  const {
    children, onOpenChild,
  } = props

  if (!children?.length) return null

  return (
    <Box width="100%" flexDirection="row" flexWrap="wrap" paddingX="small" paddingTop="xsmall" alignItems="center">
      <Text color="bodyFontLightColor" fontSize="small" marginRight="small">Children:</Text>
      {children.map((child, index) => (
        <React.Fragment key={child.assetId}>
          <Text
            as="span"
            color="blue"
            fontSize="small"
            marginRight="small"
            onClick={() => onOpenChild(child)}
            style={{ cursor: 'pointer' }}
            title={`Open ${child.name}`}
          >
            {child.name}
          </Text>
          {index < children.length - 1 && (
            <Text as="span" color="bodyFontLightColor" fontSize="small" marginRight="small">|</Text>
          )}
        </React.Fragment>
      ))}
    </Box>
  )
}

ChildrenLinks.propTypes = {
  children: PropTypes.array.isRequired,
  onOpenChild: PropTypes.func.isRequired,
}

// CH-4304: the synthetic lineage row — the base an AI asset was generated from, rendered like a
// version so compare stays in the familiar UI. Not a version: no number, delete or delivery
// caption, and it never enters isCurrentVersionDelivered or the delete gates.
const BaseItem = (props) => {
  const {
    lineage, isActive, onSelect, onOpenSource, hasSourceAsset,
  } = props

  const imageUrl = lineage.previewUrl || lineage.downloadUrl
  const isAiGenerated = Boolean(useBlobMetadata(lineage.downloadUrl)[AI_GENERATED_META_KEY])

  return (
    <ListItem boxProps={{ paddingBottom: 'large' }} flexDirection="column" disableHover>
      <Box onClick={onSelect}>
        <Image
          boxProps={{
            border: isActive ? '2px solid' : '1px solid',
            borderColor: isActive ? 'blue' : '#ececec',
            borderRadius: 5,
            backgroundSize: 'contain',
          }}
          url={imageUrl || ''}
          width="100%"
          height={182}
        >
          {!imageUrl && (
            <Box alignItems="center" color="bodyFontColor" display="grid" justifyContent="center" width="100%">
              <Icons name="Image" size={40} width="100%" />
            </Box>
          )}
        </Image>
      </Box>
      <Box
        width="100%"
        flexDirection="row"
        flexWrap="nowrap"
        paddingTop="medium"
        paddingX="small"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box width="auto" flexDirection="row" alignItems="center">
          <Text color="bodyFontLightColor" fontSize="small" marginRight="small" style={{ whiteSpace: 'nowrap' }}>
            Base — {lineage.sourceName || 'Source asset'}
          </Text>
          {isAiGenerated && (
            <Tag boxProps={{ width: 'fit-content', fontSize: 'xsmall' }}>AI</Tag>
          )}
          <Link href={lineage.downloadUrl} style={{ display: 'inline-flex', alignItems: 'center' }}>
            <FontAwesomeIcon icon={faDownload} size="sm" />
          </Link>
        </Box>
        {hasSourceAsset && (
          <Text
            as="span"
            color="blue"
            fontSize="small"
            onClick={onOpenSource}
            style={{ marginLeft: 'auto', cursor: 'pointer' }}
            title="Open the source asset"
          >
            Source
          </Text>
        )}
      </Box>
    </ListItem>
  )
}

BaseItem.propTypes = {
  lineage: PropTypes.object.isRequired,
  isActive: PropTypes.bool.isRequired,
  hasSourceAsset: PropTypes.bool.isRequired,
  onOpenSource: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
}

const VersionItem = (props) => {
  const {
    content, isActive, isLatest, serviceGroupName = null,
    canDelete, disabledReason = '', deleting, onSelect, onDelete,
    children, onOpenChild,
  } = props

  const blobUrl = content?.file?.downloadUrl || content?.assetUrl
  const isAiGenerated = Boolean(useBlobMetadata(blobUrl)[AI_GENERATED_META_KEY])

  const isImageJob = ['Photography', 'Virtual Staging'].includes(serviceGroupName)
  const imageUrl = content?.file?.previewGeneratedAt
    ? content?.previewUrl
    : (isImageJob && blobUrl ? blobUrl : '')

  return (
    <ListItem boxProps={{ paddingBottom: 'large' }} flexDirection="column" disableHover>
      <Box onClick={() => onSelect(content)}>
        <Image
          boxProps={{
            border: isActive ? '2px solid' : '1px solid',
            borderColor: isActive ? 'blue' : '#ececec',
            borderRadius: 5,
            backgroundSize: 'contain',
          }}
          url={imageUrl || ''}
          width="100%"
          height={182}
        >
          {!imageUrl && (
            <Box alignItems="center" color="bodyFontColor" display="grid" justifyContent="center" width="100%">
              <Icons name={serviceGroupName} size={40} width="100%" />
            </Box>
          )}
        </Image>
      </Box>
      <Box
        width="100%"
        flexDirection="row"
        flexWrap="nowrap"
        paddingTop="medium"
        paddingX="small"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box width="auto" flexDirection="row" alignItems="center">
          <Text color="bodyFontLightColor" fontSize="small" marginRight="small" style={{ whiteSpace: 'nowrap' }}>
            Version {content.version}{isLatest ? ' (Current)' : ''}
          </Text>
          {isAiGenerated && (
            <Tag boxProps={{ width: 'fit-content', fontSize: 'xsmall' }}>AI</Tag>
          )}
          <Link href={content.assetUrl} style={{ display: 'inline-flex', alignItems: 'center' }}>
            <FontAwesomeIcon icon={faDownload} size="sm" />
          </Link>
        </Box>
        {/* CH-4269 ships with the AI retouching set, so it stays behind the same gate */}
        {isFeatureEnabled('ai-retouching') && (
          <Box
            as="span"
            width="auto"
            display="inline-flex"
            alignItems="center"
            flexShrink={0}
            paddingLeft="medium"
            color="bodyFontLightColor"
            onClick={canDelete && !deleting ? onDelete : undefined}
            title={canDelete ? 'Delete' : disabledReason}
            style={{
              marginLeft: 'auto',
              color: canDelete ? '#e2001a' : undefined,
              cursor: canDelete && !deleting ? 'pointer' : 'not-allowed',
              opacity: canDelete ? 1 : 0.4,
            }}
          >
            <FontAwesomeIcon icon={faTrashAlt} size="sm" />
          </Box>
        )}
      </Box>
      {/* CH-4304: the AI assets generated from THIS version, as Source-style links. */}
      <ChildrenLinks children={children} onOpenChild={onOpenChild} />
    </ListItem>
  )
}

VersionItem.propTypes = {
  content: PropTypes.object.isRequired,
  isActive: PropTypes.bool.isRequired,
  isLatest: PropTypes.bool.isRequired,
  serviceGroupName: PropTypes.string,
  canDelete: PropTypes.bool.isRequired,
  disabledReason: PropTypes.string,
  deleting: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  children: PropTypes.array,
  onOpenChild: PropTypes.func,
}

// Stable fallback so the children selector never re-creates a fresh array per render.
const EMPTY_CHILDREN = []

const VersionList = () => {
  const previousAssetRef = useRef({ id: null, historyCount: 0 })

  const { callbacks, serviceJob, selectedAsset } = useContext(PageContext)
  const { toggleUpdateSelectedAsset } = callbacks || {}

  const entities = useSelector(reduxState => reduxState.entities)
  const {
    assets, assetComments, assetCommentTypes, serviceGroups,
  } = entities

  const dispatch = useThunkDispatch()

  const liveSelectedAsset = selectedAsset?.id ? (assets[selectedAsset.id] || selectedAsset) : selectedAsset
  const activeVersion = selectedAsset?.version || liveSelectedAsset?.version

  // Null for normal assets; inert-by-construction, so no gate (see features.js).
  useEffect(() => {
    if (liveSelectedAsset?.id){
      dispatch(loadAiLineage(liveSelectedAsset.id))
    }
  }, [liveSelectedAsset?.id, dispatch])

  // The CMS returns an empty DTO for assets without lineage — render only when a URL is set.
  const lineage = useSelector(reduxState => reduxState.aiLineage?.data?.[liveSelectedAsset?.id])

  // CH-4304: the AI assets generated from the viewed asset, attached to the version row they
  // came from (matched by the pinned sourceFileId the endpoint returns).
  useEffect(() => {
    if (liveSelectedAsset?.id){
      dispatch(loadAiChildren(liveSelectedAsset.id))
    }
  }, [liveSelectedAsset?.id, dispatch])

  const children = useSelector(reduxState => reduxState.aiLineage?.children?.[liveSelectedAsset?.id]) || EMPTY_CHILDREN

  const { deleting, deleteVersion } = useAssetVersionDeletion(liveSelectedAsset?.id, serviceJob?.id)

  // Content rows only — snapshot rows share a version but carry no file, so they'd break the dedupe.
  const versions = useMemo(() => {
    if (!liveSelectedAsset?.histories?.length) return []
    const contentRows = liveSelectedAsset.histories.filter(history => !history.serviceJobHistoryId)
    return [...new Map(contentRows.map(item => [item.version, item])).values()]
  }, [liveSelectedAsset])

  // Children keyed by the base file they were generated from; version rows match on their file id.
  const childrenByFileId = useMemo(() => {
    const map = {}
    children.forEach((child) => {
      if (!child.sourceFileId) return
      const list = map[child.sourceFileId] || (map[child.sourceFileId] = [])
      list.push(child)
    })
    return map
  }, [children])

  // Children whose pinned file matches no version row (base's file moved on, or an older
  // endpoint without sourceFileId) — hang them under the latest version rather than hide them.
  const orphans = useMemo(() => {
    const versionFileIds = new Set(versions.map(version => version.file?.id))
    return children.filter(child => !child.sourceFileId || !versionFileIds.has(child.sourceFileId))
  }, [children, versions])

  const latestVersion = useMemo(
    () => versions.reduce((x, y) => ((x.version > y.version) ? x : y), versions[0]),
    [versions],
  )

  // Only image jobs render the original in the marker; other jobs use the small premium/preview,
  // and video assetUrls aren't images, so preloading is worthwhile for image jobs only.
  const isImageJob = ['Photography', 'Virtual Staging'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)

  // Versions referenced by a delivered snapshot row (see DELIVERED_STATUS_ID).
  const deliveredVersions = useMemo(() => new Set(
    (liveSelectedAsset?.histories || [])
      .filter(history => history.serviceJobHistoryId && history.serviceJobStatusId >= DELIVERED_STATUS_ID)
      .map(history => history.version),
  ), [liveSelectedAsset])

  // Per version: which sources left comments — client (ClientComment) vs QC (everything else).
  const commentSourcesByVersion = useMemo(() => {
    const clientTypeId = getEntityByName(assetCommentTypes, 'ClientComment')?.id
    const sources = {}
    Object.values(assetComments || {}).forEach((comment) => {
      if (comment.assetId !== liveSelectedAsset?.id) return
      const entry = sources[comment.version] || { client: false, qc: false }
      if (comment.assetCommentTypeId === clientTypeId) entry.client = true
      else entry.qc = true
      sources[comment.version] = entry
    })
    return sources
  }, [assetComments, assetCommentTypes, liveSelectedAsset?.id])

  const selectedImage = useCallback((content) => {
    toggleUpdateSelectedAsset({
      ...liveSelectedAsset,
      assetUrl: content.assetUrl,
      previewUrl: content.previewUrl,
      premiumUrl: content.premiumUrl,
      version: content.version,
      file: {
        ...liveSelectedAsset?.file,
        downloadUrl: content.assetUrl,
        filePreviewRequests: content.filePreviewRequests || [],
        previewGeneratedAt: content.file?.previewGeneratedAt,
      },
    })
  }, [liveSelectedAsset, toggleUpdateSelectedAsset])

  // Same mechanism as a version click, but leaves `version` untouched — the reselect effect
  // below would treat a missing version as deleted and jump the user back off the base view.
  const baseSelected = Boolean(lineage?.downloadUrl && selectedAsset?.assetUrl === lineage.downloadUrl)

  const selectBase = useCallback(() => {
    if (!lineage?.downloadUrl) return
    toggleUpdateSelectedAsset({
      ...liveSelectedAsset,
      assetUrl: lineage.downloadUrl,
      previewUrl: lineage.previewUrl,
      premiumUrl: lineage.previewUrl,
      file: {
        ...liveSelectedAsset?.file,
        downloadUrl: lineage.downloadUrl,
        filePreviewRequests: liveSelectedAsset?.file?.filePreviewRequests || [],
      },
    })
  }, [lineage, liveSelectedAsset, toggleUpdateSelectedAsset])

  // CH-4304: Source/children links land on the target asset's page (URL included) with the
  // versions modal still open — selecting in place left the URL behind, so closing the modal
  // snapped back to the previously viewed asset.
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const openAsset = useCallback((asset) => {
    toggleUpdateSelectedAsset(asset)
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('assetId', asset.id)
    navigate({ search: nextParams.toString() })
  }, [toggleUpdateSelectedAsset, searchParams, navigate])

  const openSourceAsset = useCallback(() => {
    if (lineage?.sourceAssetId && assets[lineage.sourceAssetId]){
      openAsset(assets[lineage.sourceAssetId])
    }
  }, [lineage, assets, openAsset])

  const openChildAsset = useCallback((child) => {
    if (child?.assetId && assets[child.assetId]){
      openAsset(assets[child.assetId])
    }
  }, [assets, openAsset])

  const confirmAndDelete = useCallback((content) => {
    const sources = commentSourcesByVersion[content.version]
    const lines = []

    if (sources){
      const from = [sources.qc && 'QC', sources.client && 'the client'].filter(Boolean).join(' and ')
      lines.push(`This version has comments from ${from}.<br/>Deleting it will delete them too.`)
    }
    lines.push('Are you sure?')

    swal.fire({
      title: `Delete Version ${content.version}?`,
      html: lines.join('<br/><br/>'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#e2001a',
      showClass: 'slide-from-top',
    }).then(({ value }) => {
      if (value) deleteVersion(content.version)
    })
  }, [commentSourcesByVersion, deleteVersion])

  // Reselect the latest version when one is added or the selected one is gone (after a delete).
  // Skip the first render after switching assets — it already points at its own version.
  useEffect(() => {
    const historyCount = versions.length
    const currentAssetId = liveSelectedAsset?.id
    const previous = previousAssetRef.current
    previousAssetRef.current = { id: currentAssetId, historyCount }

    if (previous.id !== currentAssetId || !latestVersion) return

    const versionAdded = historyCount > previous.historyCount
    const activeMissing = !versions.some(version => version.version === activeVersion)
    if (versionAdded || activeMissing) selectedImage(latestVersion)
  }, [liveSelectedAsset?.id, versions, activeVersion, latestVersion, selectedImage])

  return (
    <>
      {versions.map((content) => {
        const isDelivered = deliveredVersions.has(content.version)
        const isOnlyVersion = versions.length <= 1
        const canDelete = !isOnlyVersion && !isDelivered
        const isLatestRow = latestVersion?.version === content.version

        return (
          <VersionItem
            key={content.id}
            content={content}
            isActive={content.version === activeVersion && !baseSelected}
            isLatest={isLatestRow}
            serviceGroupName={serviceGroups[serviceJob.serviceGroupId]?.name}
            canDelete={canDelete}
            disabledReason={isOnlyVersion ? 'At least one version must remain' : 'Versions sent to the client cannot be deleted'}
            deleting={deleting}
            onSelect={selectedImage}
            onDelete={() => confirmAndDelete(content)}
            children={[...(childrenByFileId[content.file?.id] || []), ...(isLatestRow ? orphans : [])]}
            onOpenChild={openChildAsset}
          />
        )
      }).reverse()}

      {lineage?.downloadUrl && (
        <BaseItem
          lineage={lineage}
          isActive={baseSelected}
          hasSourceAsset={Boolean(lineage.sourceAssetId && assets[lineage.sourceAssetId])}
          onSelect={selectBase}
          onOpenSource={openSourceAsset}
        />
      )}

      {/* Warm each version's original into browser cache so switching versions is instant.
          Only mounts while the panel is open, so unopened panels pay no download cost. */}
      {isImageJob && (
        <Box position="absolute" width={0} height={0} overflow="hidden" opacity={0} aria-hidden>
          {versions.map(content => (
            <img key={`preload-${content.id}`} src={content.assetUrl} alt="" />
          ))}
        </Box>
      )}
    </>
  )
}

VersionList.propTypes = {}

export default VersionList
